import { put, call, getContext, select, cancel, take } from 'redux-saga/effects';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';
import { publishOffer as publishOfferRequest } from '../../../services/publishOffer';
import { IAppState } from '../../../redux/types';
import {
  EPublishOfferErrorCode,
  OFFER_VALIDATION_FAIL,
  OFFER_VALIDATION_SUCCESS,
  publishOfferFail,
} from '../../../redux/reducers';
import { clone } from 'ramda';
import { getPublishParams } from './utils';
import { selectIsArchived, selectIsFromXml } from '../../../selectors/offer';
import { scrollToElement, sendEvent } from '../../../utils';
import { TValidationAction } from './types';
import { selectConfigValue } from '../../../selectors/config';
import { selectIsMultiPostingAvailable, selectServiceUnavailableReason } from '../../../selectors/publishTerms';
import {
  saveDraftFail,
  saveDraftStart,
  saveDraftSuccess,
  SAVE_DRAFT_FAIL,
  SAVE_DRAFT_START,
  SAVE_DRAFT_SUCCESS,
} from '../../../actions/draft';
import { PROMOTION_SECTION_ID } from '../../../constants/nodeIds';
import { PUBLISH_TERMS_SERVICE_UNAVAILABLE_REASON } from '../../../constants/events';
import { selectIsPublishToHiddenBase } from '../../../selectors/publishAvailable';

export function* saveDraft() {
  const state: IAppState = yield select();

  const unavailableReason = selectServiceUnavailableReason(state);
  const isPublishToHiddenBase = selectIsPublishToHiddenBase(state);

  if (unavailableReason && !isPublishToHiddenBase) {
    sendEvent(PUBLISH_TERMS_SERVICE_UNAVAILABLE_REASON);
    scrollToElement(PROMOTION_SECTION_ID);

    yield cancel();
  }

  yield put(saveDraftStart());

  sendEvent(SAVE_DRAFT_START);

  const validationAction: TValidationAction = yield take([OFFER_VALIDATION_SUCCESS, OFFER_VALIDATION_FAIL]);

  if (validationAction.type === OFFER_VALIDATION_FAIL) {
    const validationError = {
      errorCode: EPublishOfferErrorCode.validationError,
      errorMessage: null,
      errors: null,
    };

    yield put(publishOfferFail(validationError));
    yield put(saveDraftFail());

    yield cancel();

    return;
  }

  const httpApi: IHttpApi = yield getContext('httpApi');
  const logger: ILogger = yield getContext('logger');
  const newState: IAppState = yield select();

  const isArchived = selectIsArchived(newState);
  const isFromXml = selectIsFromXml(newState);
  const isMultiPostingAvailable = selectIsMultiPostingAvailable(newState);
  const subdomain = selectConfigValue('subdomain', newState);

  const { calltracking } = newState;

  const offer = clone(newState.offer);

  if (!isMultiPostingAvailable) {
    offer.multiposting = {
      enabled: false,
    };
  }

  const parameters = getPublishParams({
    offer,
    calltracking,
    isArchived,
    isFromXml,
  });

  const { success, statusCode, payload } = yield call(publishOfferRequest, {
    httpApi,
    logger,
    parameters,
    subdomain,
  });

  if (success) {
    yield put(saveDraftSuccess());

    sendEvent(SAVE_DRAFT_SUCCESS, payload);
  } else {
    yield put(saveDraftFail());

    sendEvent(SAVE_DRAFT_FAIL, { statusCode, payload });
  }
}
